import dayjs from 'dayjs';
import {
  DRIVER_OUTSTANDING_SOURCE_TYPE_LABEL,
  DriverOutstanding,
  DriverOutstandingStatus,
  OPEN_OUTSTANDING_STATUSES,
} from '@/types/outstanding';

// Straight from the module rather than the `@/utils` barrel — the barrel also
// re-exports the axios client, which drags an ESM-only dependency into every
// Jest suite that touches this file (see the same note in rentalDisplay.ts).
export const formatDate = (value?: string | null, fallback = '-'): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : fallback;
};

export const isOpenStatus = (status: DriverOutstandingStatus): boolean =>
  OPEN_OUTSTANDING_STATUSES.includes(status);

/**
 * Which of the three actions apply to a row, shared between the list's
 * per-row ActionDropdown and the detail page's header buttons so the two
 * screens can never disagree about what's allowed.
 */
export const availableActions = (row: DriverOutstanding) => ({
  canSettle: isOpenStatus(row.status),
  canRelease: isOpenStatus(row.status),
  // One-shot: once used, newRentalOverrideBy stays set until the driver's
  // next rental consumes it server-side — showing the action again here
  // would just re-arm something already spent.
  canOverride: isOpenStatus(row.status) && !row.newRentalOverrideBy,
});

/** Mitra name, falling back to the raw ID only when no snapshot resolved. */
export const driverLabel = (row: DriverOutstanding): string =>
  row.driverName?.trim() || `Driver #${row.driverID}`;

/** Mitra code, the second half of how ops identifies a row. */
export const driverSubLabel = (row: DriverOutstanding): string =>
  row.driverCode?.trim() || '-';

/** Where the debt came from. An unmapped source reads as its raw enum — the
 *  backend column is free text, so a new source can land before this map. */
export const sourceTypeLabel = (row: DriverOutstanding): string =>
  DRIVER_OUTSTANDING_SOURCE_TYPE_LABEL[row.sourceType] ?? row.sourceType;

/** Everything a row can be found by in the list's search box. */
export const outstandingSearchHaystack = (row: DriverOutstanding): string =>
  [row.driverName, row.driverCode, String(row.driverID)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
